import * as React from 'react';
import type { strokeDashTypes } from 'react-financial-charts';
import { isDefined, getMouseCanvas, GenericChartComponent, getStrokeDasharrayCanvas } from 'react-financial-charts';
import { generateLine, isHovering } from './index';
import type { Levels, MoreProps } from '@/types/commontypes';
import { getFillStyleWithOpacity } from '@/utils/rgbTorgba';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;
export interface FibChannelShapeProps {
  readonly startXY?: number[];
  readonly endXY?: number[];
  readonly dy?: number;
  readonly interactiveCursorClass?: string;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly fillStyle: string;
  readonly type:
    | 'XLINE' // extends from -Infinity to +Infinity
    | 'RAY' // extends to +/-Infinity in one direction
    | 'LINE'; // extends between the set bounds
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly defaultClassName?: string;
  readonly tolerance: number;
  readonly selected: boolean;
  readonly endXY2: number[] | undefined;
  readonly extendLeft: boolean;
  readonly extendRight: boolean;
  readonly channelLevels?: Levels[];
  readonly strokeDasharray: strokeDashTypes;
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onMouseDown?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly backgroundOpacity: number;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class FibChannelShape extends React.Component<FibChannelShapeProps> {
  public static defaultProps = {
    type: 'LINE',
    strokeWidth: 1,
    tolerance: 4,
    selected: false,
  };

  public render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover, onDoubleClick } = this.props;

    return (
      <ChartComponent
        isHover={this.isHover}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onDoubleClick={onDoubleClick}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { strokeStyle, strokeWidth, endXY2, channelLevels, strokeDasharray, backgroundOpacity } = this.props;
    const { line1, line2 } = helper(this.props, moreProps);

    if (line1 === undefined) return;

    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeStyle;

    if (!line2 || !endXY2) {
      ctx.beginPath();
      ctx.moveTo(line1.x1, line1.y1);
      ctx.lineTo(line1.x2, line1.y2);
      ctx.stroke();
      return;
    }

    const visibleLevels = (channelLevels ?? []).filter((level: Levels) => level.show);

    const allLines = visibleLevels.map((level: Levels) => {
      const x1 = line1.x1 + (line2.x1 - line1.x1) * level.value;
      const y1 = line1.y1 + (line2.y1 - line1.y1) * level.value;
      const x2 = line1.x2 + (line2.x2 - line1.x2) * level.value;
      const y2 = line1.y2 + (line2.y2 - line1.y2) * level.value;

      return {
        value: level.value,
        color: level.color,
        x1,
        y1,
        x2,
        y2,
      };
    });

    for (let i = 0; i < allLines.length - 1; i++) {
      const top = allLines[i];
      const bottom = allLines[i + 1];

      const colorWithOpacity = getFillStyleWithOpacity(bottom.color, backgroundOpacity);

      ctx.fillStyle = colorWithOpacity;

      ctx.beginPath();
      ctx.moveTo(top.x1, top.y1);
      ctx.lineTo(top.x2, top.y2);
      ctx.lineTo(bottom.x2, bottom.y2);
      ctx.lineTo(bottom.x1, bottom.y1);
      ctx.closePath();
      ctx.fill();
    }

    // Draw lines and labels
    allLines.forEach(({ x1, y1, x2, y2, value, color }) => {
      ctx.strokeStyle = color;
      const lineDash = getStrokeDasharrayCanvas(strokeDasharray);
      ctx.setLineDash(lineDash);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Draw % label
      const { yScale } = moreProps.chartConfig;
      const price = yScale.invert(y1); // Convert canvas Y coordinate to price
      const priceLabel = price.toFixed(2);
      const label = `${value} (${priceLabel})`;
      ctx.font = '12px Arial';
      ctx.fillStyle = color;
      ctx.textAlign = x2 > x1 ? 'left' : 'right';
      ctx.textBaseline = 'middle';
      const offset = 10;
      const labelX = x2 > x1 ? x1 - offset : x1 + offset;
      const textAlign = x2 > x1 ? 'right' : 'left';
      ctx.textAlign = textAlign;
      ctx.textBaseline = 'middle';
      const labelY = y1 - 5;
      ctx.fillText(label, labelX, labelY);
    });
  };
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onClick) {
      this.props.onClick(e, moreProps);
    }
  };

  private readonly handleMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onMouseDown) {
      this.props.onMouseDown(e, moreProps);
    }
  };
  private readonly isHover = (moreProps: MoreProps) => {
    const { tolerance, onHover } = this.props;

    if (onHover !== undefined) {
      const { line1, line2 } = getLines(this.props, moreProps);

      if (line1 !== undefined && line2 !== undefined) {
        const {
          mouseXY,
          xScale,
          chartConfig: { yScale },
        } = moreProps;

        const line1Hovering = isHovering({
          x1Value: line1.x1,
          y1Value: line1.y1,
          x2Value: line1.x2,
          y2Value: line1.y2,
          mouseXY,
          type: 'LINE',
          tolerance,
          xScale,
          yScale,
        });

        const line2Hovering = isHovering({
          x1Value: line2.x1,
          y1Value: line2.y1,
          x2Value: line2.x2,
          y2Value: line2.y2,
          mouseXY,
          type: 'LINE',
          tolerance,
          xScale,
          yScale,
        });

        return line1Hovering || line2Hovering;
      }
    }
    return false;
  };
}

function getLines(props: FibChannelShapeProps, moreProps: MoreProps) {
  const { startXY, endXY, endXY2, dy: _dy, type, extendLeft, extendRight } = props;
  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;

  if (!isDefined(startXY) || !isDefined(endXY)) {
    return {};
  }

  // Top line (first click to second click)
  const line1 = generateLine({
    type,
    start: startXY,
    end: endXY,
    xScale,
    yScale,
    extendLeft,
    extendRight,
  });

  let line2;
  if (isDefined(endXY2) && endXY && startXY) {
    // Get same vector as line1
    const dx = endXY[0] - startXY[0];
    const dyLine = endXY[1] - startXY[1];

    const bottomEnd: [number, number] = [endXY2[0] + dx, endXY2[1] + dyLine];

    line2 = generateLine({
      type,
      start: endXY2,
      end: bottomEnd,
      xScale,
      yScale,
      extendLeft,
      extendRight,
    });
  }

  return {
    line1,
    line2,
  };
}

function helper(props: FibChannelShapeProps, moreProps: MoreProps) {
  const lines = getLines(props, moreProps);
  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;

  const line1 =
    lines.line1 !== undefined
      ? {
          x1: xScale(lines.line1.x1),
          y1: yScale(lines.line1.y1),
          x2: xScale(lines.line1.x2),
          y2: yScale(lines.line1.y2),
        }
      : undefined;

  const line2 =
    lines.line2 !== undefined
      ? {
          x1: xScale(lines.line2.x1),
          y1: yScale(lines.line2.y1),
          x2: xScale(lines.line2.x2),
          y2: yScale(lines.line2.y2),
        }
      : undefined;

  return {
    lines,
    line1,
    line2,
  };
}
